import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchWorknumberApi } from "../../../../../api/client";
import { mapHistory } from "./worknumber.mapping";
import type { WorkNumberHistory } from "./worknumber.types";
import { worknumberKeys } from "./worknumberKeys";

/**
 * Shared queryOptions for an applicant's previously-pulled Work Number history
 * (key + queryFn). Backed by the generated WorkbenchWorknumberApi.getHistory,
 * whose `object` response is mapped to table rows by `mapHistory`. This is the
 * fallback read when the applicant is NOT eligible to pull fresh data
 * (eligibility `canPull === false`) — if prior history exists it is displayed.
 *
 * Disabled until both ids are present and the caller opts in.
 * @param {string} approvalId - The deal / approval identifier (path param).
 * @param {string} applicantId - The applicant identifier (path param).
 * @param {boolean} enabled - Caller opt-in; combined with the presence guards.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function worknumberHistoryQuery(approvalId: string, applicantId: string, enabled = true) {
  return queryOptions({
    queryKey: worknumberKeys.history({ approvalId, applicantId }),
    queryFn: async ({ signal }): Promise<WorkNumberHistory> => {
      const response = await workbenchWorknumberApi.getHistory(approvalId, applicantId, { signal });
      return mapHistory(response.data);
    },
    staleTime: 30_000,
    // The history read is the fallback probe when the applicant can't pull fresh
    // data — like eligibility, an error is a definitive answer, so don't retry.
    retry: false,
    enabled: enabled && !!approvalId && !!applicantId,
  });
}

/**
 * Reads an applicant's previously-pulled Work Number history. Disabled until the
 * approval id and applicant id are present and the caller enables it (typically
 * only once eligibility has resolved to `canPull === false`).
 * @param {string} approvalId - The deal / approval identifier.
 * @param {string} applicantId - The applicant identifier.
 * @param {{ enabled?: boolean }} [options] - Caller opt-in (defaults to enabled).
 * @param {boolean} [options.enabled] - When false, the query never fires.
 * @returns {ReturnType<typeof useQuery<WorkNumberHistory>>} The TanStack Query result.
 */
export function useWorknumberHistory(approvalId: string, applicantId: string, options?: { enabled?: boolean }) {
  return useQuery(worknumberHistoryQuery(approvalId, applicantId, options?.enabled ?? true));
}
